import { extractTextFromTipTap } from "../../../src/shared/schemas/CharacterSchema.js";

/**
 * @param {{ value?: unknown }} field
 * @returns {boolean}
 */
export function isCustomFieldTraitBlank(field) {
  const v = field?.value;
  if (v == null) return true;
  if (typeof v === "string") {
    if (v.trim() === "") return true;
    const textWithoutHtml = v.replace(/<[^>]+>/g, "");
    return textWithoutHtml.trim() === "";
  }
  return extractTextFromTipTap(v).trim() === "";
}

/**
 * @typedef {Object} MergeCustomFieldsWithTemplateOrderOptions
 * @property {boolean} [dropBlankExtrasNotInTemplate]
 * @property {boolean} [insertMissingTemplateFields=true] When false, do not insert rows for template labels missing from the saved entity (respects user removals; avoids duplicate trait rows after renames).
 */

/**
 * Reorders existing traits to follow `templateFields` label order (e.g. Customize defaults modal),
 * optionally inserts rows for labels present on the template but missing on the card, and appends traits whose labels are not in the template.
 *
 * When `dropBlankExtrasNotInTemplate` is true (default), blank traits whose labels are not in the
 * template are omitted so obsolete template rows from a prior role do not linger after a role change.
 * Traits with a blank label are always kept: they are in-progress rows right after "Add Trait".
 *
 * @template T
 * @param {T[]} currentFields
 * @param {Array<Pick<T, "label"> & Partial<T>>} templateFields
 * @param {(tf: Pick<T, "label"> & Partial<T>) => T} createFieldForMissingTemplateLabel
 * @param {MergeCustomFieldsWithTemplateOrderOptions} [options]
 * @returns {T[]}
 */
export function mergeCustomFieldsWithTemplateOrder(
  currentFields,
  templateFields,
  createFieldForMissingTemplateLabel,
  options = {},
) {
  const { dropBlankExtrasNotInTemplate = true, insertMissingTemplateFields = true } = options;
  if (!Array.isArray(templateFields) || templateFields.length === 0) {
    return currentFields;
  }
  const templateLabels = new Set(templateFields.map((tf) => tf.label));
  const used = new Set();
  const ordered = [];
  for (const tf of templateFields) {
    const hit = currentFields.find((f) => f.label === tf.label && !used.has(f.id));
    if (hit) {
      ordered.push(hit);
      used.add(hit.id);
    } else if (insertMissingTemplateFields) {
      ordered.push(createFieldForMissingTemplateLabel(tf));
    }
  }
  for (const f of currentFields) {
    if (used.has(f.id)) continue;
    // Blank label means the user just clicked "Add Trait" and has not named the row yet.
    // Those drafts must not be stripped while template merge runs (same tick as add).
    const labelTrimmed = String(f.label ?? "").trim();
    const dropStaleBlankExtra =
      dropBlankExtrasNotInTemplate &&
      !templateLabels.has(f.label) &&
      isCustomFieldTraitBlank(f) &&
      labelTrimmed !== "";
    if (dropStaleBlankExtra) {
      continue;
    }
    ordered.push(f);
  }
  return ordered;
}
